import { TabListProps as MuiTabListProps, TabContext, TabPanel } from "@mui/lab";
import { SxProps, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TabList from "./components/TabList";
import { tabPanelSxMap } from "./config";
import { Tab, TabsVariant } from "./types";

export type TabLayoutProps = {
  tabs: Tab[];
  defaultTab: string;
  sx?: SxProps;
  withSearchParams?: boolean;
  renderNavigationComponent?: (navigation: JSX.Element, tabValue: string) => JSX.Element;
  renderContent?: (panels: JSX.Element[], tabValue: string) => JSX.Element;
  variant?: TabsVariant;
  tabListProps?: MuiTabListProps;
};

const TabLayout = ({
  tabs,
  defaultTab,
  sx,
  withSearchParams = false,
  renderNavigationComponent,
  renderContent,
  variant = "standard",
  tabListProps,
}: TabLayoutProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]);
  const { tab: tabParam } = params as {
    tab?: string;
    scrollTop?: string;
  };

  const getInitialTab = (): string => {
    if (!withSearchParams) return defaultTab;

    const initTabValue = tabs.find((tab) => tab.id === tabParam);
    if (initTabValue) return initTabValue.id;

    return defaultTab;
  };

  const [tabValue, setTab] = useState<string>(getInitialTab());

  useEffect(() => {
    if (withSearchParams && tabParam !== tabValue) {
      const { scrollTop, ...restParams } = params;
      setSearchParams(
        qs.stringify({
          ...restParams,
          tab: tabValue,
        }),
        { replace: true }
      );
    }
  }, [tabValue]);

  useEffect(() => {
    if (tabParam && tabParam !== tabValue) setTab(tabParam);
  }, [searchParams]);

  const tabList = (
    <TabList
      tabs={tabs}
      onChange={(newTabId) => setTab(newTabId)}
      variant={variant}
      tabListProps={tabListProps}
    />
  );

  const panels = tabs.map((tabPanel) => (
    <TabPanel
      key={tabPanel.id}
      value={tabPanel.id}
      sx={
        {
          p: 0,
          width: "100%",
          ...tabPanelSxMap[variant],
          ...tabPanel.sx,
        } as SxProps<Theme>
      }>
      {tabPanel.component}
    </TabPanel>
  ));

  return (
    <Box sx={sx}>
      <TabContext value={tabValue}>
        {renderNavigationComponent ? renderNavigationComponent(tabList, tabValue) : tabList}
        {renderContent ? renderContent(panels, tabValue) : panels}
      </TabContext>
    </Box>
  );
};

export default TabLayout;
